/**
 * Change log (OP#1930) -- server-state hooks for GET /api/v2/audit and /audit/chain.
 *
 * Backend: api/v2/audit_log.py. Newest first, keyset-paginated (`next_cursor` = the id to pass
 * as ?cursor= for the next, older page), so "Load older" is an infinite query. Scope rides on
 * the header customer/site exactly like the other list pages (org_id NARROWS server-side; a
 * site outside reach is a 404). Which actions and fields a role sees is decided server-side.
 * Kept in its own module so the types do not bloat hooks.ts.
 */
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { api } from "./client";
import { useSession } from "@/stores/session";
import { auditQuery, type AuditChainStatus, type AuditFilters, type AuditPage } from "@/lib/auditView";

/** Rows per request. The grid renders every loaded row; "Load older" fetches the next page. */
export const AUDIT_PAGE_SIZE = 50;

/** The change log for the header scope plus the in-page filters. */
export function useAuditLog(filters: AuditFilters) {
  const orgId = useSession((s) => s.currentOrgId);
  const siteId = useSession((s) => s.currentSiteId);
  const query = auditQuery(filters, { orgId, siteId });
  return useInfiniteQuery({
    // Scope-prefixed like every other key (queryKeys.ts convention), so a scope switch never
    // shows the previous customer's rows under the new header.
    queryKey: ["v2", orgId ?? "_", siteId ?? "_", "audit", query] as const,
    queryFn: ({ pageParam, signal }) =>
      api.get<AuditPage>(
        "/audit",
        { ...query, limit: AUDIT_PAGE_SIZE, cursor: pageParam ?? undefined },
        signal,
      ),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.next_cursor,
    enabled: !!orgId,
    staleTime: 15_000,
  });
}

/** Hash-chain verification for the header customer (platform_admin only; 403 otherwise, so
 *  the page only asks when the session says platform_admin). */
export function useAuditChain(enabled: boolean) {
  const orgId = useSession((s) => s.currentOrgId);
  return useQuery({
    queryKey: ["v2", orgId ?? "_", "_", "audit-chain"] as const,
    queryFn: ({ signal }) => api.get<AuditChainStatus>("/audit/chain", { org_id: orgId }, signal),
    enabled: enabled && !!orgId,
    // It re-hashes the org's whole chain: never poll, fetch on demand.
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });
}
